import { Observable, of } from 'rxjs';
import { catchError, map, flatMap } from 'rxjs/operators';
import {
    HttpClient,
    HttpHeaders,
    HttpParams,
    HttpRequest,
    HttpResponse
} from '@angular/common/http';
import { BackendService } from 'app/services/backend.service';
import { 
    Injectable,
    Injector
} from '@angular/core';
import * as rs from 'jsrsasign';
import * as JsEncryptModule from 'jsencrypt';
import * as CryptoJS_AES from 'crypto-js/aes';
import * as CryptoJS_EncHex from 'crypto-js/enc-hex';
import * as CryptoJS_Enc_Utf8 from 'crypto-js/enc-utf8';
import * as CryptoJS_NoPad from 'crypto-js/pad-nopadding';

type encFieldType = 'pin' | 'password' | 'formdata';

export interface RSAE2EKeyResponse {
    modulus: string,
    exponent: string
}

export interface PostE2eKeyRequest {
    encryptedPayload: string,
    encKeyCheckDigit: string,
    hmacKeyCheckDigit: string,
    algorithm: string
}

@Injectable({
    providedIn: 'root'
  })
export class E2eService {

    sRKey = 'eventid';
    eKey: string;
    preMasterSecLen = 128;
    eKeyLen = 32;
    ivLen = 16;
    saltLen = 32;
    hKeyLen = 32;
    iv: string;
    hKey: string;
    CR: string;
    sRToEnc: string;
    rsaE2eKey: any;
    publicKey: any;
    sRKeyVal: string;
    e2eKeyUrl = 'e2e/key';
    hashAlgo = 'HmacSHA256';
    initError = true;
    initErrorMsg = '';
    e2eConfigUrl = 'e2eConfigUrl';
    e2eConfig :any;

    private encFieldTypeValueMap: any = {
        // reverse the keys to find data type
        nip: 'a',
        drowssap: 'b',
        atadmrof: 'c'
    }

    constructor(private _http: HttpClient,
        private _injector: Injector) {
    }

    get backend(): BackendService {
        return this._injector.get<BackendService>(BackendService);
    }
    
    private performKeyExchange(headers) {
        return this.backend.get(this.e2eKeyUrl, null, headers)
            .pipe(
                catchError((error: any) => {
                    return of(false);
                }),
                map((rsaE2eResponse) => {
                    if (rsaE2eResponse) {
                        this.rsaE2eKey = rsaE2eResponse.body;
                        try {
                            this.publicKey = rs.KEYUTIL.getKey({ n: this.rsaE2eKey.modulus, e: this.rsaE2eKey.exponent });
                            return this.publicKey;
                        } catch (e) {
                            return of(false);
                        }
                    }
                }),
                flatMap(() => {
                    const encAesKey = this.getEncryptedKey();
                    const ekCD = this.generateCheckDigit(this.eKey);
                    const hkCD = this.generateCheckDigit(this.hKey);
                    this.CR = this.generateCR();
                    // The cstype field was empty string. Hence this can be ignored
                    const e2eKeyRequest: PostE2eKeyRequest = {
                        encryptedPayload: encAesKey,
                        encKeyCheckDigit: ekCD,
                        hmacKeyCheckDigit: hkCD,
                        algorithm: 'AES'
                    };
                    return this.backend.post(this.e2eKeyUrl, e2eKeyRequest, headers)
                        .pipe(
                            catchError((error: any) => {
                                return of(false);
                            }),
                            map(data => {
                                (data && data.headers) ? this.sRKeyVal = data.headers.get(this.sRKey) : this.sRKeyVal = '';
                                if (this.isSRValid()) {
                                    return of(true);
                                } else {
                                    return of(false);
                                }
                            })
                        );

                })
            )
    }

    // This function creates a secure random hex string of given bytes
    private getSecureRandomHex(n: number) {
        return rs.crypto.Util.getRandomHexOfNbytes(n);
    }

    // This method returns RSA Encrypted EncryptionKey + InitVector + HashKey
    private getEncryptedKey(): string {
        const preMasterSec: string = this.getSecureRandomHex(this.preMasterSecLen),
            salt = preMasterSec.substring(0, 64),
            ek_ikm = preMasterSec.substring(64, 128),
            iv_ikm = preMasterSec.substring(128, 192),
            hk_ikm = preMasterSec.substring(192, 256);
        this.iv = this.getHMAC256Hash(iv_ikm, salt).substring(0, 32);
        this.eKey = this.getHMAC256Hash(ek_ikm, salt);
        this.hKey = this.getHMAC256Hash(hk_ikm, salt);
        const keyStr = this.eKey + this.iv + this.hKey;
        let encKeyStr;
        try {
            let jsencrypt = new JsEncryptModule.JSEncrypt();
            const publicKeyPEM = rs.KEYUTIL.getPEM(this.publicKey);
            jsencrypt.setPublicKey(publicKeyPEM);
            encKeyStr = jsencrypt.encrypt(keyStr);
            return rs.b64nltohex(encKeyStr);
            // encKeyStr = rs.crypto.Cipher.encrypt(keyStr, this.publicKey);
            // return encKeyStr;
        } catch (e) {

            //throw new Error('Error occured while encrypting the AES Key');
            console.error('Error occured while encrypting the AES Key');
        }
    }

    private generateCheckDigit(encryptionkey: string): string {
        try {
            const dataToEnc = ' ',
                checDigitLen = 6,
                paddingLen = 16,
                padDataToEnc = this.padMsgString(dataToEnc, paddingLen),
                encData = CryptoJS_AES.encrypt(padDataToEnc, CryptoJS_EncHex.parse(encryptionkey),
                    {
                        iv: CryptoJS_EncHex.parse(this.iv),
                        padding: CryptoJS_NoPad
                    }),
                encDataInHex = encData.ciphertext.toString(CryptoJS_EncHex);
            return encDataInHex.substring(0, checDigitLen);
        } catch (e) {
            throw new Error('Error occured while creating check digit');
        }

    }

    public etd(dataToEnc: string, encDataType: encFieldType): string {
        try {
            // Before enecrypting data use the SR in window session storage
            // TODO: The SR has to be fetched from session storage before encryption
            const sensitiveMsg = this.CR + this.getDecryptedSR() + this.encFieldTypeValueMap[encDataType.toString().split('').reverse().join('')] + dataToEnc,
                encSensitiveMsgHex = this.aesEncDataHex(sensitiveMsg),
                cipherBase64 = this.getBase64FromHex(encSensitiveMsgHex),
                cipherHmac = this.getHMAC256Hash(encSensitiveMsgHex, this.hKey),
                cipherHmacBase64 = this.getBase64FromHex(cipherHmac),
                encData = cipherBase64 + '.' + cipherHmacBase64;
            return encData;
        } catch (e) {
            throw new Error(e);
        }
    }

    public decryptData(dataToDec: string): string {

        const cipherDotMac = dataToDec.split('.'),
            cipherBase64 = cipherDotMac[0],
            cipherHMacBase64 = cipherDotMac[1],
            cipher = rs.b64nltohex(cipherBase64),
            remoteHmac = rs.b64nltohex(cipherHMacBase64),
            localHmac = this.getHMAC256Hash(cipher, this.hKey);
        if (remoteHmac !== localHmac) {
            throw new Error('Decryption failed server hash and client hash not matching');
        } else {
            const decData = this.decryptDataWithAesKey(cipher),
                resCR = decData.substring(0, 32),
                resSR = decData.substring(32, 64),
                resData = decData.substring(64, decData.length);
            if (resSR === this.sRToEnc && resCR === this.CR) {
                // Client Random and Server Random matches
                // Hence the data from response is the actual data
                return resData;
            } else {
                throw new Error('Decryption failed - client random and server random did not match');
            }
        }
    }

    private decryptDataWithAesKey(encData: string): string {
        // Assumption encData will be in Hex
        try {
            const decData = CryptoJS_AES.decrypt({ ciphertext: CryptoJS_EncHex.parse(encData) }, CryptoJS_EncHex.parse(this.eKey),
                {
                    iv: CryptoJS_EncHex.parse(this.iv)
                }).toString(CryptoJS_Enc_Utf8);
            return decData;
        } catch (e) {
            throw new Error('Error occured while decrypting data with AES Key');
        }

    }

    // This method can be replaced once string.padEnd() is available in typescript
    private padMsgString(msg: string, padLen: number) {
        const paddingChar = ' ';
        for (let i = 0; i < padLen; i++) {
            msg += paddingChar;
        }
        return msg;
    }

    // This method would decryot the SR from response header so that decrypted SR can be used to encrypt data
    // This method returns true if SR is valid
    private isSRValid(): boolean {
        try {
            const encSR = this.sRKeyVal;
            let cipherDotMac, cipherBase64, cipherHMacBase64, cipher, remoteHmac, localHmac;
            if (encSR) {
                cipherDotMac = encSR.split('.');
                cipherBase64 = cipherDotMac[0];
                cipherHMacBase64 = cipherDotMac[1];
                cipher = rs.b64nltohex(cipherBase64);
                remoteHmac = rs.b64nltohex(cipherHMacBase64);
                localHmac = this.getHMAC256Hash(cipher, this.hKey);
                if (remoteHmac !== localHmac) {
                    this.initErrorMsg = 'client SR HMAC not matching with Server SR HMAC';
                    return false;
                } else {
                    this.sRToEnc = this.decryptDataWithAesKey(cipher);
                    if (this.sRToEnc === undefined && this.sRToEnc === '') {
                        this.initErrorMsg = 'error occured while decrypting SR';
                        return false;
                    } else {
                        return true;
                    }
                }
            } else {
                return false;
            }

        } catch (e) {
            this.initErrorMsg = 'error occured while decrypting SR';
            return false;
        }
    }

    // This method is used to create Client Random of 32 bytes
    private generateCR(): string {
        this.CR = this.getSecureRandomHex(16);
        return this.CR;
    }

    private aesEncDataHex(dataToEnc: string): string {
        // The ciphertext you get back after encryption isn't a string yet. It's a CipherParams object.
        const encData = CryptoJS_AES.encrypt(dataToEnc, CryptoJS_EncHex.parse(this.eKey),
            {
                iv: CryptoJS_EncHex.parse(this.iv)
            });

        return encData.ciphertext.toString(CryptoJS_EncHex);
    }

    private getHMAC256Hash(dataToHash: string, hashKey: string): string {

        // handle error scenaio while creating hash
        const mac = new rs.KJUR.crypto.Mac({ alg: this.hashAlgo, 'pass': { 'hex': hashKey } });
        mac.updateHex(dataToHash);
        const macHex = mac.doFinal();
        return macHex;
    }

    private getBase64FromHex(hexData: string): string {
        return rs.hextob64(hexData);
    }

    private getDecryptedSR() {
        if (this.isSRValid()) {
            return this.sRToEnc;
        } else {
            throw new Error('Invalid SR');
        }
    }

    public initE2e(headers={}): Observable<any> {
        return this.performKeyExchange(headers);
    }

    public getE2eConfig(){
        return this.backend.get(this.e2eConfigUrl, null, {})
            .pipe(
                catchError((error: any) => {
                    return of(false);
                }),
                map((response) => {
                    if (response) {
                        this.e2eConfig = response.body;
                        return of(true); 
                    }
                }),
            )
    }

    public enytmId(tmId, endpoint): string {
        if(this.e2eConfig && this.e2eConfig.globalE2eFlag && this.e2eConfig.e2eEnabledUri.includes(endpoint)) {
            tmId = this.etd(tmId, 'formdata');
        }
        return tmId;
    }

  public setEventId(eventId: string): void {
    if (this.sRKeyVal) {
            this.sRKeyVal = eventId;
            if (!(this.isSRValid())) {
                this.initE2e();
            }
    } else {
          this.initE2e();
        }
    }

}


